import { EffectBus, USAGE_IOC_KEY, type UsageRanker } from '@hypercomb/core'
import type {
  BootstrapHistoryLike,
  LineageLike,
  LocalizationLike,
  NavigationLike,
  TreeLoggerLike,
} from './shell-contracts'
import { collectProximity } from './proximity-registry'
import './install-monitor'
// Escape's single owner. A side effect, not a surface — it registers the IoC
// facade the essentials Escape cascade asks for, so no window has to claim the
// key for itself (ui/tool-windows.ts).
// TOOL WINDOWS ARE THE SHELL'S. This used to be a side-effect import of
// `../ui/tool-windows`, which is the only line that ever tied the runtime to
// hypercomb-shared/ui. Each shell imports it now (web, dev and the shim all
// do), so the escape cascade behaves exactly as before and this package
// depends on nothing above it.
import { Store } from './store'
import { initializeTextThemePool } from './text-theme-pool'

// Note: the legacy layer-tree materializers (materializeInstalledLayers,
// materializeStructure) and their helpers (readLayerNode, applyLayerToDir)
// were removed. They mirrored layer.children as folders under hypercomb.io/
// and __structure__/ — a parallel-store violation now that layers are the
// only source of truth for hierarchy. Render reads layer.children directly;
// no on-disk mirror is needed or wanted.

export type RuntimeInitializerOptions = {
  logOpfs?: boolean
  /**
   * Where host locale catalogs come from. Return null for a locale you cannot
   * supply and the loader treats it as absent, exactly like a failed import.
   *
   * Omit it and the shipped catalogs below are used — which is what the web
   * and dev shells do, unchanged. A SHIM passes a resolver instead, because a
   * locale is CONTENT: it lives on a host in a pool of meaning and resolves by
   * signature like everything else, rather than being bundled into the shell
   * or handed out as an installer resource. Fourteen catalogs are 2.9 MB;
   * bundling them puts every language into every host's entry bundle to serve
   * the one language a visitor actually reads.
   */
  catalogs?: (locale: string) => Promise<Record<string, string> | null>
  /** Which locales exist, for the post-paint idle warm. Omit it and only the
   *  active locale plus the 'en' fallback ever load — which is the right
   *  default for a host, where a language is fetched when someone picks it. */
  locales?: () => readonly string[]
}

// Dev-build defaults, applied on a LOOPBACK origin only (the operator's own
// dev machine) and only when the value is unset — so two localhost tabs join
// the same swarm (same host + same room secret) with zero manual setup, while
// a real deployed origin (non-loopback) is never affected. An env.js
// HYPERCOMB_DEV_HOST still overrides the host; an explicit clear still empties
// the secret. The mesh relay's own loopback default (nostr-mesh.drone) lands
// on the same jwize.com relay.
const DEV_DEFAULT_HOST = 'jwize.com'
const DEV_DEFAULT_SECRET = 'downtown'

// Reset the former flat-top rollout once, then preserve every explicit
// participant choice made after this revision. Keeping the revision separate
// from `hc:hex-orientation` makes point-top a real default without turning it
// into a forced setting on every boot.
const HEX_ORIENTATION_DEFAULT_REVISION = 'point-top-2026-07-29'

// Idempotent: callers in different parts of the boot sequence (web's
// main.ts, then App constructor → CoreAdapter.initialize) used to each
// call initializeRuntime, double-registering EffectBus listeners and
// double-loading translations. Cache the in-flight / completed promise
// and return it to subsequent callers.
let _initializeRuntimePromise: Promise<void> | null = null

export const initializeRuntime = async (
  options: RuntimeInitializerOptions = {},
): Promise<void> => {
  if (_initializeRuntimePromise) return _initializeRuntimePromise
  _initializeRuntimePromise = _runInitializeRuntime(options)
  return _initializeRuntimePromise
}

const _runInitializeRuntime = async (
  options: RuntimeInitializerOptions = {},
): Promise<void> => {
  const {
    logOpfs = false,
  } = options

  try {
    if (localStorage.getItem('hc:hex-orientation-default-revision')
      !== HEX_ORIENTATION_DEFAULT_REVISION) {
      localStorage.setItem('hc:hex-orientation', 'point-top')
      localStorage.setItem(
        'hc:hex-orientation-default-revision',
        HEX_ORIENTATION_DEFAULT_REVISION,
      )
    }
  } catch {
    // Storage-free sessions already use the in-memory point-top default.
  }

  // Every participant has a host. Three cases, resolved in order:
  //
  //   1. Real domain origin (jwize.com, alice.dev) — auto-bootstrap to the
  //      page's origin. Casual visitors and operators-in-production both
  //      get the right value with zero config.
  //
  //   2. Loopback origin + a `window.HYPERCOMB_DEV_HOST` global set by the
  //      shell's env.js — auto-bootstrap to that. This is how an operator
  //      tells their dev shell "I am jwize.com, even though the browser
  //      loaded me from localhost:4250." env.js is gitignored so the value
  //      is per-developer, not committed.
  //
  //   3. Loopback origin and no dev-host global — default to DEV_DEFAULT_HOST
  //      (jwize.com) so the operator's dev tabs auto-resolve against the host
  //      with zero config. The mesh-modal still lets them change it.
  try {
    const rawOrigin = String(window.location.origin ?? '')
    const isLoopback = /^https?:\/\/(localhost|127(?:\.\d+){3}|\[?::1\]?)(:|\/|$)/i.test(rawOrigin)
    const normalize = (raw: string): string => raw
      .replace(/^wss?:\/\//i, '')
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '')
      .toLowerCase()

    // ── host (self-domain) ──
    if (!localStorage.getItem('hc:nostrmesh:self-domain')) {
      let candidate = ''
      if (!isLoopback) {
        // Case 1 — real domain. Use the page's origin.
        candidate = normalize(rawOrigin)
      } else {
        // Case 2 — env.js per-developer override (gitignored); Case 3 — the
        // dev-build default host.
        const devHost = normalize(String((window as { HYPERCOMB_DEV_HOST?: string }).HYPERCOMB_DEV_HOST ?? ''))
        candidate = devHost || DEV_DEFAULT_HOST
      }
      if (candidate) localStorage.setItem('hc:nostrmesh:self-domain', candidate)
    }

    // ── beta ramp: live relay + byte mirrors (real origins only) ──
    // On a DEPLOYED origin, seed hc:nostrmesh:use-live-relay='1' ONCE so the
    // whole beta routing is explicit and stable rather than merely implied by
    // the unset origin-default: this single flag steers BOTH the mesh
    // (→ wss://jwize.com, nostr-mesh.drone loadRelays) AND ContentBroker's byte
    // fallback (→ jwize.com + pluginthematrix.io, #liveRelayActive). The
    // one-time marker (hc:beta-relay-seeded) means a later `/use-live-relay
    // clear` is respected — we never re-seed over an explicit clear. Loopback
    // dev is never seeded (it resolves from the local relay; force with
    // `/use-live-relay on` to test the live path). Opt out on a real origin
    // anytime with `/use-live-relay off`. Revisit when third-party operators
    // federate (no MANDATORY central chokepoint).
    if (!isLoopback
        && localStorage.getItem('hc:nostrmesh:use-live-relay') === null
        && localStorage.getItem('hc:beta-relay-seeded') === null) {
      localStorage.setItem('hc:nostrmesh:use-live-relay', '1')
      localStorage.setItem('hc:beta-relay-seeded', '1')
    }

    // ── location secret (dev-build default) ──
    // On loopback, seed the room secret so two localhost tabs land in the same
    // swarm room without typing it. Respects an explicit clear; never touches
    // a real origin.
    if (isLoopback) {
      const cleared = (() => { try { return localStorage.getItem('hc:secret-cleared') === '1' } catch { return false } })()
      const secretStore = get('@hypercomb.social/SecretStore') as { value: string; set: (s: string) => void } | undefined
      if (secretStore && !secretStore.value && !cleared) secretStore.set(DEV_DEFAULT_SECRET)
    }
  } catch { /* private mode — readers handle the empty case */ }

  if (logOpfs) {
    const logger = get('@hypercomb.social/OpfsTreeLogger') as TreeLoggerLike | undefined
    await logger?.log?.()
  }

  const store = get('@hypercomb.social/Store') as Store | undefined
  await store?.initialize?.()
  ;(window as any).__hcBoot?.('store.initialize done')
  // Settings read local text-theme creations from their meaning pool. Load
  // it after first paint; the in-memory shipped defaults answer until then.
  if (store) {
    const load = (): void => {
      void initializeTextThemePool(store).catch(err =>
        console.warn('[text-themes] could not read the pool', err))
    }
    const idle = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback
    if (idle) idle(load, { timeout: 3000 })
    else setTimeout(load, 250)
  }

  // Legacy layer-tree materialization removed. Layers are the only source
  // of truth for hierarchy; no on-disk mirror.

  // Load host translations for the i18n service — LAZILY. Only the ACTIVE
  // locale (?lang → hc:locale → browser, resolved by LocalizationService's
  // constructor and exposed as i18n.locale) plus the 'en' fallback are needed
  // before first paint; importing + registering all 14 catalogs here was pure
  // pre-paint weight. The remaining catalogs load in a detached idle task.
  // A locale SWITCH to a not-yet-loaded catalog is safe twice over:
  //   1. setLocale emits 'locale:changed' (EffectBus) → we load that catalog
  //      immediately, without waiting for the idle sweep; and
  //   2. registerTranslations dispatches the service's coalesced 'change'
  //      event, so even a catalog that lands AFTER the switch re-renders
  //      every `t` binding the moment it registers (keys show untranslated
  //      only for the brief import gap — same as any cold catalog fetch).
  const i18n = get('@hypercomb.social/I18n') as LocalizationLike | undefined
  if (i18n) {
    const loadedLocales = new Set<string>()
    const resolveCatalog = options.catalogs
    const loadLocale = async (locale: string): Promise<void> => {
      if (loadedLocales.has(locale)) return
      // A supplied resolver REPLACES the shipped catalogs rather than
      // supplementing them: two sources for one locale is two answers to
      // "what does this key say", and the shim's whole point is that the
      // answer arrives as verified content.
      if (resolveCatalog) {
        loadedLocales.add(locale)
        try {
          const catalog = await resolveCatalog(locale)
          if (catalog) i18n.registerTranslations('app', locale, catalog)
        } catch { /* unreachable locale — graceful degradation */ }
        return
      }
      // No resolver, no host catalogs. The runtime ships none of its own —
      // a locale is content, and which languages exist is the HOST's answer,
      // not this package's. Shells that still bundle theirs pass
      // `bundledCatalogs` from hypercomb-shared.
    }

    // Eager: fallback + active. 'en' is the service's hard fallback locale
    // (i18n.service.ts FALLBACK_LOCALE) — #resolve consults it for every
    // missing key, so it must be present at first paint regardless of locale.
    await Promise.all([loadLocale('en'), loadLocale(i18n.locale)])
    ;(window as any).__hcBoot?.('i18n catalogs loaded', 'active+en eager, rest idle-deferred')

    // Switch-safety: load the target catalog the moment the locale changes.
    EffectBus.on<{ locale: string }>('locale:changed', ({ locale }) => { void loadLocale(locale) })

    // Detached idle task for the remaining catalogs — never on the paint path.
    // NOT for a published site's visitor: "idle" arrives while the landing
    // content is still on the wire, and the sweep's catalogs (~50–75 KB each,
    // compressed) took its bandwidth on a slow link (measured 2026-09-28: four
    // landed before the cover). A visitor's language still loads on demand.
    if ((window as { __HC_READONLY__?: boolean }).__HC_READONLY__ !== true) {
      const loadRest = (): void => {
        // With a resolver in play the shell has no list of its own — the host
        // publishes which locales exist, so there is nothing to sweep here.
        // Every locale still loads on demand via 'locale:changed' above.
        // Nothing to sweep: the shell owns the list of locales, and every one
        // still loads on demand via 'locale:changed' above.
        for (const locale of options.locales?.() ?? []) void loadLocale(locale)
      }
      const ric = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback
      if (typeof ric === 'function') ric(loadRest, { timeout: 3000 })
      else setTimeout(loadRest, 250)
    }

    // User override layer — a CONTENT-ADDRESSED document in the
    // sign('overrides') document pool, shape { "<locale>": { "<key>":
    // "<value>" } }. Loaded after defaults so savvy users/consumers can
    // shadow any key without editing the shipped catalogs. Read the pool
    // first, then the legacy non-signed `overrides/i18n.json` (Store's boot
    // absorb content-addresses it into the pool and removes it).
    try {
      let json: Record<string, Record<string, string>> | null = null
      const poolBuf = store?.overrides ? await store.getPoolDoc(store.overrides) : null
      if (poolBuf) {
        try { json = JSON.parse(new TextDecoder().decode(poolBuf)) } catch { json = null }
      }
      if (!json) {
        const root = await navigator.storage.getDirectory()
        const dir = await root.getDirectoryHandle(Store.LEGACY_OVERRIDES_DIRECTORY, { create: false }).catch(() => null)
        const fileHandle = await dir?.getFileHandle('i18n.json', { create: false }).catch(() => null)
        if (fileHandle) {
          try { json = JSON.parse(await (await fileHandle.getFile()).text()) } catch { json = null }
        }
      }
      if (json) {
        for (const [locale, catalog] of Object.entries(json)) {
          if (catalog && typeof catalog === 'object') {
            i18n.registerOverrides('app', locale, catalog)
          }
        }
      }
    } catch { /* no overrides or malformed — ignore silently */ }
  }

  const lineage = get('@hypercomb.social/Lineage') as LineageLike | undefined
  await lineage?.initialize?.()
  ;(window as any).__hcBoot?.('lineage.initialize done')

  // Boot-time data preload. Two-path access discipline says renders
  // should hit the cache exclusively; bag scans are the cold-miss
  // fallback only. To make that real, warm the cache here BEFORE the
  // first drone heartbeat — by the time show-cell asks for layers,
  // they're already in `#preloaderCache` / `#parsedLayerCache`.
  //
  // Step 1: `preloadAllBags` indexes every bag's head marker (cheap
  //          enumerate + one read per bag). After this, every lineage's
  //          head sig is in cache.
  // Step 2: `preloadFromRoot(rootSig)` walks layer.children from the
  //          root, fetching every reachable descendant via the
  //          cache-first `getLayerBySig` primitive. Cold descendants
  //          land in cache as the walk finds them.
  //
  // Logs in both methods show counts + timing so a stuck preload is
  // visible. Failures are non-fatal: cold render is correct, just slower.
  const historyService = get('@HistoryService') as {
    preloadAllBags?: () => Promise<void>
    preloadFromRoot?: (rootSig: string) => Promise<void>
    preloadNeighbourhood?: (locationSig: string, maxDepth?: number, segments?: readonly string[], levelsAway?: number) => Promise<void>
    preloadAncestors?: (segments: readonly string[], depth?: number, gen?: number) => Promise<void>
    preloadGeneration?: () => number
    sign?: (l: { explorerSegments: () => readonly string[] }) => Promise<string>
    latestMarkerSigFor?: (lineageSig: string, name: string) => Promise<string>
  } | undefined
  console.log('[preload] runtime-initializer reached preload step. history methods:', {
    historyExists: !!historyService,
    preloadAllBags: typeof historyService?.preloadAllBags,
    preloadFromRoot: typeof historyService?.preloadFromRoot,
    sign: typeof historyService?.sign,
    latestMarkerSigFor: typeof historyService?.latestMarkerSigFor,
  })
  // NO global preload. The old idle-deferred `preloadAllBags()` enumerated
  // every bag + every marker (O(hive): ~30s on a 1200-bag hive) and its OPFS
  // churn starved the first render's own reads. Heads are now resolved ON
  // DEMAND, one bag at a time (`currentLayerAt` → `#warmLineageHead`: one dir
  // listing + one marker read), and the head index grows incrementally as
  // lineages are touched. A bounded, passive warmer can drip the current
  // neighbourhood's heads behind the shell; the full marker history is read
  // only when working with history. There is no brute-force pass on boot.
  void historyService
  ;(window as any).__hcBoot?.('history preload skipped (on-demand head resolution)')

  const navigation = get('@hypercomb.social/Navigation') as NavigationLike | undefined
  navigation?.listen?.()

  // Walk the cell tree from root to current URL, loading markers at each depth.
  // encounter() calls find() → reads markers → loads bees → pulses them.
  const history = get('@hypercomb.social/BootstrapHistory') as BootstrapHistoryLike | undefined
  await history?.run?.()
  ;(window as any).__hcBoot?.('BootstrapHistory.run done (Phase-1 URL restore; bees load in Phase-2 background)')

  // ── Bounded neighbourhood pre-warm ──────────────────────────────────
  // 79c36e63 removed the whole-hive substrate walk that (as a SIDE EFFECT)
  // warmed the click targets, so the first interaction at any location was cold
  // and show-cell's completeness gate held the canvas blank until the neighbour
  // layers landed (project_boot_first_click_warming; also the "post-adopt
  // nothing shows" symptom). This is the bounded, passive warmer the disabled
  // global preload above alludes to: on boot and on every navigation, breadth-
  // warm the CURRENT location's bounded subtree (depth ≤ 3) via the already-
  // bounded, cooperatively-sliced preloadFromRoot — NOT the O(hive) walk that
  // starved paint. Debounced so a nav/fs burst coalesces; idle-deferred so it
  // never steals the paint it's warming for; HistoryService dedups on head sig.
  // Non-fatal throughout.
  if (lineage && typeof historyService?.preloadNeighbourhood === 'function') {
    const idle = (cb: () => void): void => {
      const ric = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback
      if (typeof ric === 'function') ric(cb, { timeout: 1200 })
      else setTimeout(cb, 200)
    }
    // Proximity is warmed as ONE pattern with the current location: after the
    // current subtree, warm every one-click destination a visible surface has
    // DECLARED (collections cards, website cards, launch-group members — see
    // proximity-registry). The current location re-warms every nav (its head may
    // have moved); each declared destination warms ONCE per session (its lineage
    // sig is stable — content changes surface as a fresh sig, or as the current-
    // location warm when the user actually arrives). Capped per pass so a huge
    // declared set drains over several navs instead of flooding one idle slice.
    const warmedProximity = new Set<string>()
    const PROXIMITY_WARM_CAP = 16
    // REAL-TIME SUPERSEDES PRELOADER — enforced, not aspirational. OPFS is
    // effectively a single service queue: a subtree warm that runs while the
    // just-navigated location is still painting queues hundreds of reads IN
    // FRONT of the render's own props/image reads and starves first paint
    // (measured on a large hive: 21s click→paint with the warm racing the
    // render). So before warming, wait for the first SETTLED render pass
    // after the nav — the paint the user is waiting on — bounded so a pass
    // that never settles can't wedge the warmer.
    const awaitSettledPaint = (): Promise<void> => new Promise<void>((resolve) => {
      let done = false
      let off: (() => void) | undefined
      const finish = (): void => {
        if (done) return
        done = true
        try { off?.() } catch { /* already gone */ }
        clearTimeout(timer)
        resolve()
      }
      const timer = setTimeout(finish, 2500)
      let replay = true
      const maybeOff = EffectBus.on?.('render:cell-count', (p: { settled?: boolean } | undefined) => {
        // First emission is the last-value REPLAY (the PREVIOUS location's
        // pass) — skip it; only a fresh pass proves the new location painted.
        if (replay) { replay = false; return }
        if (p?.settled !== false) finish()
      })
      off = typeof maybeOff === 'function' ? maybeOff : undefined
    })
    const warmCurrent = (): void => {
      void (async () => {
        // The direction as it stands NOW, before the forward warm: the way
        // back and the declared destinations below are warmed only while it
        // still holds.
        let gen = 0
        try {
          await awaitSettledPaint()
          gen = historyService.preloadGeneration?.() ?? 0
          const sig = await lineage.currentSig()
          // Hand the PATH over, not only its signature: a sig cannot be
          // un-hashed, so without it the warm can only assume it is standing at
          // the hive root — and every view it prepares for a deeper location is
          // prepared under the wrong address.
          const segs = (lineage.explorerSegments?.() ?? []).map(s => String(s ?? '').trim()).filter(Boolean)
          if (sig) await historyService.preloadNeighbourhood!(sig, undefined, segs)
          // THE WAY BACK IS PART OF THE NEIGHBOURHOOD. Arrive at a deep
          // location by a route that skipped the ancestors (a reload, a deep
          // link, a reference portal, an adopt) and going back finds nothing
          // loaded — the subtree warm only ever pointed downhill. Warm the
          // ancestors too, nearest first, one level each (a layer's pack
          // carries every sibling's visual, so one level IS the whole page
          // back-navigation paints).
          if (segs.length && (historyService.preloadGeneration?.() ?? gen) === gen) {
            await historyService.preloadAncestors?.(segs, undefined, gen)
          }
        } catch { /* non-fatal: a cold render is correct, just slower */ }
        try {
          // Rank declared destinations by local usage so the participant's
          // most-used one-click targets survive the per-pass cap and warm
          // first. Absent tracker → original order (a cold participant).
          const declared = await collectProximity()
          const ranker = get(USAGE_IOC_KEY) as UsageRanker | undefined
          const proximate = ranker ? ranker.rank(declared) : declared
          // ABANDON ON NAVIGATION. Each warm below bails internally when the
          // participant moves, but the SEQUENCE did not: the loop marched on to
          // the next declared destination, so a stale sweep kept queuing OPFS
          // reads in front of the render of the location the participant had
          // just entered. Capture the generation and stop the whole sweep the
          // moment it moves — the new location's own warm re-declares what is
          // still worth warming.
          let warmed = 0
          for (const psig of proximate) {
            if ((historyService.preloadGeneration?.() ?? gen) !== gen) {
              console.log('[preload] proximity sweep abandoned — navigation superseded it')
              break
            }
            if (warmedProximity.has(psig)) continue
            if (warmed >= PROXIMITY_WARM_CAP) {
              console.log(
                `[preload] proximity warm capped at ${PROXIMITY_WARM_CAP} this pass; ` +
                `${proximate.length} declared, remainder drains on the next navigations`,
              )
              break
            }
            warmedProximity.add(psig)
            warmed++
            // One click away: its own face is within reach, its children's are not.
            await historyService.preloadNeighbourhood!(psig, undefined, undefined, 1)
          }
        } catch { /* non-fatal: a cold render is correct, just slower */ }
      })()
    }
    let warmTimer: ReturnType<typeof setTimeout> | null = null
    const scheduleWarm = (): void => {
      if (warmTimer) clearTimeout(warmTimer)
      warmTimer = setTimeout(() => { warmTimer = null; idle(warmCurrent) }, 180)
    }
    lineage.addEventListener('change', scheduleWarm)
    idle(warmCurrent)  // boot: warm the restored location once first paint is past
  }

  // console.log('[runtime-initializer] ioc keys:', list())

  // pivot: restore persisted state + handle toggle command
  let pivotOn = localStorage.getItem('hc:hex-pivot') === 'true'
  if (pivotOn) {
    EffectBus.emit('render:set-pivot', { pivot: true })
  }
  EffectBus.on<{ cmd: string }>('keymap:invoke', ({ cmd }) => {
    if (cmd !== 'render.togglePivot') return
    pivotOn = !pivotOn
    localStorage.setItem('hc:hex-pivot', String(pivotOn))
    EffectBus.emit('render:set-pivot', { pivot: pivotOn })
  })

  // orientation: J switches between the existing point-top default and
  // flat-top, persisting the participant's explicit choice.
  EffectBus.on<{ cmd: string }>('keymap:invoke', ({ cmd }) => {
    if (cmd !== 'render.toggleOrientation') return
    const flat = localStorage.getItem('hc:hex-orientation') !== 'flat-top'
    localStorage.setItem('hc:hex-orientation', flat ? 'flat-top' : 'point-top')
    EffectBus.emit('render:set-orientation', { flat })
  })

  // mesh: toggle public/private on keymap command
  EffectBus.on<{ cmd: string }>('keymap:invoke', ({ cmd }) => {
    if (cmd !== 'mesh.togglePublic') return
    const current = localStorage.getItem('hc:mesh-public') === 'true'
    const next = !current

    // Going public needs somewhere to go public IN. The swarm composes its
    // sig from (path, room, secret) and refuses to subscribe or publish
    // without both credentials — so flipping the flag with a half-set zone
    // produces a hive that LOOKS joined (swarm chrome, relay socket) and is
    // deaf and mute. Nothing seeds a room on a bare-domain origin, so that
    // was the default result of this shortcut there. Send the participant to
    // the selector instead; confirming it emits 'mesh:join', which flips the
    // flag through the same path the swarm control uses. Leaving is never
    // gated — the safe direction always works on the first press.
    if (next) {
      const room = (get('@hypercomb.social/RoomStore') as { value?: string } | undefined)?.value?.trim() ?? ''
      const secret = (get('@hypercomb.social/SecretStore') as { value?: string } | undefined)?.value?.trim() ?? ''
      if (!room || !secret) {
        EffectBus.emit('mesh:open-modal', { join: true })
        return
      }
    }

    localStorage.setItem('hc:mesh-public', String(next))
    // ANNOUNCE, DON'T REACH IN. Going public is driven by
    // `mesh:public-changed` — the mesh owns its own network flag and the swarm
    // owns its subscriptions, and both react to this. A direct
    // `setNetworkEnabled(next, true)` used to sit here as well, which also
    // ran `reconnectAll()`: it tore the relay sockets down and resubscribed
    // them in the same instant the swarm was opening its own subscription for
    // the new zone, and the events the relay had already replayed were lost
    // with them. Measured, not theorised — with that call live, the
    // two-client harness dropped from 27/27 to 12/26, a joining participant
    // seeing one of its peer's two tiles. It had been dormant since the
    // hypercomb-runtime carve-out (the '@NostrMeshDrone' lookup resolved
    // nothing), so "does nothing here" is the behaviour the swarm has been
    // built and verified against. Nothing in this package needs the mesh.
    EffectBus.emit('mesh:public-changed', { public: next })
  })
}
